// PAM (SSH) on the shared policy screens (see ../adapterContract.js).
// Who = console users by email and directory groups across AD and Entra;
// Where = Gateway (optional), hosts (onboarded endpoints), accounts and sudo.
// The only product with Approval and Session (under Advanced); Fallback too.
// The detail page shows the policy's grants (keys) and connect commands.
//
// Moved here from the old modal (enduser/ssh-proxy/policy/SshPolicyManager):
// host and account pickers, scanned and admin accounts, sudo with its checks,
// the "another policy holds this grant" warning (inline, before saving),
// Allow for administrators only, suspend = revokePolicy (ends grants).
import React from "react";

import { DOMAIN_ID, ORG_ID, USER_EMAIL } from "../../../constants";
import { isAdminUser } from "../../../utils/helpers";
import {
  createSSHPolicy,
  deleteSSHPolicy,
  errorText,
  listSSHPolicy,
  resumeSSHPolicy,
  suspendSSHPolicy,
  updateSSHPolicy,
} from "../../../service/sshPolicyService";
import { callService, defineAdapter, policyFailure, updatedPolicyId } from "../adapterContract";
import { searchConsoleUsers, searchDirectoryGroups } from "./ssh/directory";
import {
  HOST_RE,
  SSH_POLICY_TYPE,
  accountProblem,
  accountText,
  buildSudo,
  cleanList,
  realId,
  sshSpecificity,
  sudoErrors,
  sudoFields,
  sudoServerError,
  sudoText,
  whereText,
} from "./ssh/sshModel";
import { ActionExtra, GrantsSummary, ListRedirects, Where, gatewayName, useSshData } from "./ssh/sections";

const ids = () => ({ orgId: ORG_ID(), tenantId: DOMAIN_ID() });
const me = () => {
  try {
    return (typeof USER_EMAIL === "function" && USER_EMAIL()) || "";
  } catch {
    return "";
  }
};

// The list/suspend/resume/delete services take their fields at the top level,
// not in `payload`.
const flat = (fn, args) =>
  new Promise((resolve, reject) =>
    fn({ ...args, onSuccess: (...r) => resolve(r[0]), onFailure: (e) => reject(policyFailure(e)) }),
  );

// A 400 about ssh.sudo ("ssh.sudo: …", with or without "Invalid policy: ")
// becomes an "invalid" failure whose reason names it; sudoServerError puts it
// in plain words (the shell shows its generic line until it asks adapters).
const withSudoReason = (p) =>
  p.catch((f) => {
    const text = f?.reason || errorText(f?.raw) || "";
    if (sudoServerError(text)) {
      return Promise.reject({ ...f, kind: "invalid", reason: String(text).replace(/^Invalid policy:\s*/i, ""), friendly: sudoServerError(text) });
    }
    return Promise.reject(f);
  });

// A host preset from the URL (?host=, from endpoint onboarding and the
// gateway's Policy tab).
const presetHost = () => {
  try {
    const h = new URLSearchParams(window.location.search).get("host");
    return h && HOST_RE.test(h) ? [h] : [];
  } catch {
    return [];
  }
};

const words = (list, fmt = (x) => x) => (list.length ? list.map(fmt).join(", ") : "");

const sshAdapter = defineAdapter({
  key: "ssh",
  policyType: SSH_POLICY_TYPE,
  modeKey: "mfa_pam",
  labels: {
    product: "SSH",
    eyebrow: "PAM · Policies",
    listDescription: "Who may reach which accounts on which endpoints, and whether they need MFA.",
    emptySentence: "Without a policy, sessions are refused.",
    subject: "session",
  },

  list: () => flat(listSSHPolicy, ids()).then((rows) => (Array.isArray(rows) ? rows : [])),
  create: ({ policyName, policyJson, force }) =>
    withSudoReason(callService(createSSHPolicy, { ...ids(), policyName, policyJson, ...(force ? { force: true } : {}) })).then((res) => ({
      id: realId(typeof res?.data === "string" ? res.data : null),
      warning: res?.warning || "",
    })),
  // Update writes a new version with a new id (updatedPolicyID).
  update: ({ id, policyName, policyJson }) =>
    withSudoReason(callService(updateSSHPolicy, { id, ...ids(), policyName, policyJson, updatedBy: me() })).then((res) => ({
      id: realId(updatedPolicyId(res, id)) || id,
      warning: res?.warning || "",
    })),
  // Suspend ends the policy's grants (revokes their keys); resume re-issues them.
  setActive: (row, active) => flat(active ? resumeSSHPolicy : suspendSSHPolicy, { ...ids(), policyId: row.id }),
  remove: (row) => flat(deleteSSHPolicy, { ...ids(), policyId: row.id }),

  emptyDraft: () => ({
    who: { everyone: false, users: [], groups: [] },
    product: { policyId: null, storedFlow: null, jumpServers: [], hosts: presetHost(), accounts: [], ...sudoFields(null) },
  }),
  fromPolicy: (row) => {
    const j = row?.policyJson || {};
    const ssh = j.ssh || {};
    return {
      who: { everyone: !!ssh.matchAll, users: cleanList(ssh.users), groups: cleanList(ssh.groups) },
      product: {
        policyId: row?.id ?? null,
        // A stored policy with no policyFlow is enforced as Allow.
        storedFlow: j.policyFlow || "allow",
        jumpServers: (ssh.jumpServers || []).map(Number).filter(Boolean),
        hosts: cleanList(ssh.hosts),
        accounts: cleanList(ssh.accounts),
        ...sudoFields(ssh.sudo),
      },
    };
  },
  toPolicyJson: (draft, stored) => {
    const s = stored || {};
    const p = draft.product;
    const everyone = !!draft.who.everyone;
    const ssh = {
      ...(s.ssh || {}),
      users: everyone ? [] : cleanList(draft.who.users),
      groups: everyone ? [] : cleanList(draft.who.groups),
      matchAll: everyone,
      hosts: cleanList(p.hosts),
      accounts: cleanList(p.accounts),
    };
    // No gateway filter / no sudo: the key is left out of a new policy. An
    // edit is merged one level deep onto the stored ssh block, so one that had
    // them clears them explicitly ([] and null).
    const gw = (p.jumpServers || []).map(Number).filter(Boolean);
    if (gw.length) ssh.jumpServers = gw;
    else if (s.ssh?.jumpServers !== undefined) ssh.jumpServers = [];
    else delete ssh.jumpServers;
    const sudo = buildSudo(p);
    if (sudo) ssh.sudo = sudo;
    else if (s.ssh?.sudo) ssh.sudo = null;
    else delete ssh.sudo;
    // The shell writes policyFlow; it is always explicit, because update
    // stores policyJson as given and an absent policyFlow is enforced as Allow.
    return { ...s, policyType: SSH_POLICY_TYPE, policyName: draft.policyName.trim(), ssh };
  },
  validate: (draft) => {
    const e = {};
    const p = draft.product;
    const hosts = cleanList(p.hosts);
    const accounts = cleanList(p.accounts);
    // Grants are issued only for the hosts and accounts named.
    if (!hosts.length) e["where.hosts"] = "Pick at least one host.";
    else {
      const bad = hosts.find((h) => !HOST_RE.test(h));
      if (bad) e["where.hosts"] = `${bad} isn't a host or host:port.`;
    }
    if (!accounts.length) e["where.accounts"] = "Add at least one account.";
    else {
      const bad = accounts.map(accountProblem).find(Boolean);
      if (bad) e["where.accounts"] = bad;
    }
    Object.assign(e, sudoErrors(p));
    // No second factor at all: administrators only, unless it was stored so.
    if (draft.action === "allow" && p.storedFlow !== "allow" && !isAdminUser()) {
      e["action.flow"] = "Only administrators can choose Allow. Choose Require MFA or Deny.";
    }
    return e;
  },

  useData: useSshData,
  Where,
  who: {
    everyone: true,
    except: true,
    fields: [
      { key: "users", kind: "users", label: "Users", placeholder: "Search console users by email", search: (q) => searchConsoleUsers(q), except: "users" },
      { key: "groups", kind: "groups", label: "Groups", placeholder: "Search AD and Entra groups", search: (q) => searchDirectoryGroups(q), except: "groups" },
    ],
  },

  listWhere: (row) => whereText(row?.policyJson?.ssh),
  sentenceWhere: (draft, data) => {
    const p = draft.product;
    const hosts = cleanList(p.hosts);
    const accounts = cleanList(p.accounts).map(accountText);
    const gw = (p.jumpServers || []).map((id) => gatewayName(data, id));
    const few = (l) => {
      if (l.length > 3) return `${l.slice(0, 2).join(", ")} or ${l.length - 2} more`;
      return l.length <= 1 ? l.join("") : `${l.slice(0, -1).join(", ")} or ${l[l.length - 1]}`;
    };
    return [`to ${hosts.length ? few(hosts) : "no hosts yet"}`, accounts.length ? `as ${few(accounts)}` : "", gw.length ? `through ${gw.join(", ")}` : ""]
      .filter(Boolean)
      .join(" ");
  },
  reviewRows: (draft, data) => {
    const p = draft.product;
    const gw = (p.jumpServers || []).map((id) => gatewayName(data, id));
    const rows = [
      { key: "gateway", label: "Gateway", value: gw.length ? gw.join(", ") : "Each host's own gateway", section: "where" },
      { key: "hosts", label: "Hosts", value: words(cleanList(p.hosts)) || "None picked", section: "where" },
      { key: "accounts", label: "Accounts", value: words(cleanList(p.accounts), accountText) || "None picked", section: "where" },
      { key: "sudo", label: "Sudo", value: sudoText(buildSudo(p)), section: "where" },
    ];
    // Read-only pages only: an unsaved draft has no grants yet.
    if (data?.page === "view" && p.policyId) {
      rows.push({ key: "grants", label: "Grants", value: <GrantsSummary draft={draft} data={data} />, section: "action" });
    }
    return rows;
  },

  features: {
    fallback: true,
    approval: { search: (q) => searchConsoleUsers(q) },
    session: true,
    privilegeHint: "Admin = root",
    specificity: (item) => sshSpecificity(item?.row?.policyJson),
    ActionExtra,
    ListHeaderExtra: ListRedirects,

  },
});

export default sshAdapter;
